import { useState } from "react";
import { Lightbulb, ChevronDown, ChevronUp, X } from "lucide-react";

const CONTENT = {
  "skill-matrix": {
    en: {
      title: "Getting Started with Skill Matrix",
      tips: [
        "Add crew members first in the Crew section, then assign skills and proficiency levels here.",
        "Click any cell to set proficiency (1-5 stars). Use the search bar to filter by skill or crew member name.",
        "Use 'AI Suggest' to get training recommendations based on gaps in your team's skill coverage.",
      ],
    },
    es: {
      title: "Primeros Pasos con la Matriz de Habilidades",
      tips: [
        "Agrega miembros del equipo primero en la seccion de Crew, luego asigna habilidades y niveles de competencia aqui.",
        "Haz clic en cualquier celda para establecer la competencia (1-5 estrellas). Usa la barra de busqueda para filtrar.",
        "Usa 'Sugerencias IA' para obtener recomendaciones de capacitacion basadas en brechas de tu equipo.",
      ],
    },
    de: {
      title: "Erste Schritte mit der Skill-Matrix",
      tips: [
        "Fuge zuerst Teammitglieder im Crew-Bereich hinzu, dann weise hier Fahigkeiten und Kompetenzstufen zu.",
        "Klicke auf eine Zelle, um die Kompetenz festzulegen (1-5 Sterne). Nutze die Suche zum Filtern.",
        "Nutze 'KI-Vorschlage' für Schulungsempfehlungen basierend auf Kompetenzlucken in deinem Team.",
      ],
    },
  },
  "rate-cards": {
    en: {
      title: "Getting Started with Rate Cards & Cost Analysis",
      tips: [
        "Rate Cards define what you PAY your crew per role (hourly/daily). Create cards for different project types or clients.",
        "Cost Analysis compares your crew costs against what you charge clients. 'Linked' items match rate card roles to catalog services.",
        "'Unlinked' services are catalog items not yet matched to any rate card role — link them to track profit margins automatically.",
      ],
    },
    es: {
      title: "Primeros Pasos con Tarifas y Análisis de Costos",
      tips: [
        "Las Tarifas definen lo que PAGAS a tu equipo por rol (por hora/dia). Crea tarjetas para diferentes tipos de proyecto.",
        "El Análisis de Costos compara tus costos de equipo contra lo que cobras. Los items 'Vinculados' conectan roles con servicios del catálogo.",
        "Los servicios 'No Vinculados' son items del catálogo sin tarjeta asignada — vincularlos para rastrear margenes automaticamente.",
      ],
    },
    de: {
      title: "Erste Schritte mit Honorarkarten & Kostenanalyse",
      tips: [
        "Honorarkarten definieren, was du deinem Team pro Rolle ZAHLST (stundlich/taglich). Erstelle Karten für verschiedene Projekttypen.",
        "Die Kostenanalyse vergleicht deine Teamkosten mit dem, was du Kunden berechnest. 'Verknupfte' Items verbinden Rollen mit Katalogdiensten.",
        "'Nicht verknupfte' Dienste sind Katalog-Items ohne zugewiesene Honorarkarte — verknupfe sie, um Gewinnmargen automatisch zu verfolgen.",
      ],
    },
  },
  "rate-intelligence": {
    en: {
      title: "Getting Started with Rate Intelligence",
      tips: [
        "Browse industry-standard pricing across 36 verticals. Switch regions (Americas/DACH/Africa) to see localized benchmarks.",
        "Click any industry card to compare YOUR rates vs. market averages. Export branded PDF rate cards to share with clients.",
        "Rates are refreshed weekly by our AI Research Agent (every Sunday 3 AM UTC). Use 'Refresh Rates' for an immediate manual update.",
      ],
    },
    es: {
      title: "Primeros Pasos con Inteligencia de Tarifas",
      tips: [
        "Explora precios estandar de la industria en 36 verticales. Cambia de region (Americas/DACH/Africa) para ver referencias locales.",
        "Haz clic en cualquier tarjeta para comparar TUS tarifas vs. promedios del mercado. Exporta tarjetas PDF con tu marca para clientes.",
        "Las tarifas se actualizan semanalmente por nuestro Agente de Investigación IA (domingos 3 AM UTC). Usa 'Actualizar' para una actualizacion inmediata.",
      ],
    },
    de: {
      title: "Erste Schritte mit Rate Intelligence",
      tips: [
        "Durchsuche branchenubliche Preise in 36 Branchen. Wechsle Regionen (Americas/DACH/Afrika) für lokale Benchmarks.",
        "Klicke auf eine Branchenkarte, um DEINE Preise mit dem Marktdurchschnitt zu vergleichen. Exportiere gebrandete PDF-Preislisten.",
        "Preise werden wochentlich durch unseren KI-Forschungsagenten aktualisiert (Sonntag 3 Uhr UTC). Nutze 'Aktualisieren' für sofortige Updates.",
      ],
    },
  },
  "mailing-studio": {
    en: {
      title: "Getting Started with Mailing Studio",
      tips: [
        "Your audience grows automatically. Subscribers from your website, landing pages, and waitlist all flow here with proper tags.",
        "Create campaigns with the visual email editor, AI generation, or pick from templates. Schedule sends or trigger instantly.",
        "Set up automations to nurture leads automatically. Use 'Add to Automation' in campaigns to reuse your best emails.",
      ],
    },
    es: {
      title: "Primeros Pasos con el Estudio de Correo",
      tips: [
        "Tu audiencia crece automaticamente. Los suscriptores de tu sitio web, landing pages y lista de espera llegan aqui con las etiquetas correctas.",
        "Crea campanas con el editor visual, generacion con IA, o elige plantillas. Programa envios o envia al instante.",
        "Configura automatizaciones para nutrir leads automaticamente. Usa 'Agregar a Automatización' en campanas para reutilizar tus mejores correos.",
      ],
    },
    de: {
      title: "Erste Schritte mit dem Mailing Studio",
      tips: [
        "Dein Publikum wachst automatisch. Abonnenten von deiner Website, Landing Pages und Warteliste fliessen hier mit den richtigen Tags ein.",
        "Erstelle Kampagnen mit dem visuellen Editor, KI-Generierung oder wahle aus Vorlagen. Plane Versand oder sende sofort.",
        "Richte Automatisierungen ein, um Leads automatisch zu pflegen. Nutze 'Zur Automatisierung hinzufugen' in Kampagnen, um deine besten E-Mails wiederzuverwenden.",
      ],
    },
  },
};

export function GettingStartedTips({ section, language = "en" }) {
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const content = CONTENT[section]?.[language] || CONTENT[section]?.en;
  if (!content || dismissed) return null;

  return (
    <div className="rounded-xl border border-[var(--app-accent)]/15 bg-[var(--app-accent)]/5 overflow-hidden" data-testid={`getting-started-${section}`}>
      <div className="flex items-center justify-between px-4 py-2.5">
        <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-2 flex-1 text-left">
          <Lightbulb size={14} className="text-[var(--app-accent)] shrink-0" />
          <span className="text-xs font-semibold text-[var(--app-text)]">{content.title}</span>
          {expanded ? <ChevronUp size={12} className="text-[var(--app-text-muted)]" /> : <ChevronDown size={12} className="text-[var(--app-text-muted)]" />}
        </button>
        <button onClick={() => setDismissed(true)} className="text-[var(--app-text-muted)] hover:text-[var(--app-text)] p-1 -mr-1" data-testid={`dismiss-tips-${section}`}>
          <X size={12} />
        </button>
      </div>
      {expanded && (
        <div className="px-4 pb-3 space-y-1.5">
          {content.tips.map((tip, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-[var(--app-text-secondary)]">
              <span className="font-mono text-[var(--app-accent)] shrink-0 mt-0.5 text-[10px]">{i + 1}.</span>
              <span>{tip}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
